import {
  Align,
  Backdrop,
  Button,
  Container,
  Distribute,
  Icon,
  IconButton,
  Spacer,
  SvgChevronSmallDown,
  SvgClose,
  SvgEditBorder,
  SvgSend,
  SvgUndo,
  Text,
  TextButton,
} from '@loomhq/lens';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Confetti from 'react-dom-confetti';
import { useDispatch, useSelector } from 'react-redux';
import SvgInline from 'react-svg-inline';

import { showErrorBar } from 'actions/errorBar';
import { toggleFirstVideoShareModalIsOpen } from 'actions/shareVideoModal';
import { ERROR } from 'constants/error-severities';
import {
  FIRST_VIDEO_SHARE_MODAL_COPY_BUTTON_CLICKED,
  FIRST_VIDEO_SHARE_MODAL_COPY_BUTTON_DROPDOWN_CLICKED,
  FIRST_VIDEO_SHARE_MODAL_RERECORD_BUTTON_CLICKED,
} from 'constants/events';
import { EXP_FIRST_VIDEO_SHARE_MODAL } from 'constants/featureFlag';
import { DESKTOP, EXTENSION } from 'constants/recordingClients';
import { EMBED_VIDEO, LOOM_URI } from 'constants/routes';
import { SHOW_FIRST_VIDEO_SHARE_MODAL } from 'constants/triggers';
import { updateVideoNameCreator } from 'creators/shareVideo';
import { completeTriggerCreator } from 'creators/triggers';
import * as analytics from 'utilities/analytics';
import { requestExtensionOpenIfInstalled } from 'utilities/extension';
import { getGraphQLClient } from 'utilities/graphql';
import * as logger from 'utilities/loggerx';
import { getParam } from 'utilities/url';

import { useFlagIsActivated } from 'hooks/featureFlag';
import { useShouldShowTrigger } from 'hooks/triggers';
import { selectFirstVideoShareModalIsOpen } from 'selectors/firstVideoShareModal';
import {
  selectRecordingClient,
  selectUserIsOwner,
  selectVideo,
} from 'selectors/shareVideo';

import IsUsersOnlyVideo from './isUsersOnlyVideo.graphql';

import CopyLinkSection from 'components/share-page-common/copy-link';
import TitleBar from 'components/share-page-common/title-bar';
import { getCopyLinkText } from 'components/share-video/share-buttons';
import ShareModal from 'components/share-video/share-modal';

import styles from './styles.module.less';

const SPRITZ_SVG = `
<svg width="45" height="48" viewBox="0 0 45 48" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="5.31055" y="25" width="9" height="9" transform="rotate(36.1582 5.31055 25)" fill="#ED785E"/>
<rect x="42.1055" y="22.2783" width="4.4105" height="4.4105" transform="rotate(60.1786 42.1055 22.2783)" fill="#ED785E"/>
<circle cx="25.5" cy="33.5" r="2.5" fill="#FFC65C"/>
<circle cx="23.5" cy="14.5" r="2.5" fill="#FFC65C"/>
<circle cx="20.5" cy="45.5" r="2.5" fill="#625DF5"/>
<circle cx="10" cy="5" r="2.5" fill="#625DF5"/>
</svg>
`;

const ShareFirstVideoModalWrapper = () => {
  const [isOnlyVideo, setIsOnlyVideo] = useState(false);
  const currentUserIsOwner = useSelector(selectUserIsOwner);
  const recordingClient = useSelector(selectRecordingClient);
  const video = useSelector(selectVideo);
  const isOpen = useSelector(selectFirstVideoShareModalIsOpen);
  const shouldShowTrigger = useShouldShowTrigger(SHOW_FIRST_VIDEO_SHARE_MODAL);
  const dispatch = useDispatch();

  const acceptedClients = [DESKTOP, EXTENSION];

  const isQA = currentUserIsOwner => {
    const param = getParam('qa');

    return param === 'share-modal' && currentUserIsOwner;
  };

  useEffect(() => {
    async function isFirstVideoCheck() {
      try {
        const client = getGraphQLClient();

        const { data } = await client.query({
          query: IsUsersOnlyVideo,
          variables: { videoId: video.id },
        });

        const isOnlyVideo = data.response.isOnlyVideo;

        if (!isOnlyVideo) {
          // need to update user record so we don't make this api request again
          dispatch(completeTriggerCreator(SHOW_FIRST_VIDEO_SHARE_MODAL));
        }

        setIsOnlyVideo(isOnlyVideo);
      } catch (err) {
        logger.error(err, {
          message: 'Error fetching is only video status for user',
          videoId: video.id,
        });
      }
    }

    if (currentUserIsOwner && shouldShowTrigger && video.id) {
      isFirstVideoCheck();
    }
  }, [currentUserIsOwner, shouldShowTrigger, video, dispatch]);

  const isTriggeredOrAlreadyOpen = shouldShowTrigger || isOpen;
  const isAcceptedClients = acceptedClients.includes(recordingClient);

  const shouldShowModal =
    (currentUserIsOwner &&
      isTriggeredOrAlreadyOpen &&
      isOnlyVideo &&
      isAcceptedClients) ||
    isQA(currentUserIsOwner);

  return shouldShowModal ? (
    <ShareFirstVideoModal video={video} recordingClient={recordingClient} />
  ) : null;
};

const ConfirmRedoFooter = ({ setConfirmRedo, redo }) => {
  return (
    <Distribute isSpread>
      <div>
        <Text variant="title">Are you sure you’d like to re-record?</Text>
        <Text size="large">You can access this video later.</Text>
      </div>
      <div>
        <Spacer isInline right={2}>
          <Button size="large" onClick={() => setConfirmRedo(false)}>
            Cancel
          </Button>
        </Spacer>
        <Button size="large" variant="primary" onClick={redo}>
          Yes, re-record
        </Button>
      </div>
    </Distribute>
  );
};

const NextStepsCard = ({ onDismiss }) => {
  return (
    <>
      <div className={styles.nextStepsCard}>
        <Icon icon={<SvgSend />} />
        <Spacer top={2} bottom={0.5}>
          <Text fontWeight="black" size="xlarge">
            Next steps, share your{' '}
            <span className={styles.underline}>video link</span>
          </Text>
        </Spacer>
        <Text>Paste the link into Slack, email, or wherever you work.</Text>
        <Spacer top={1} />
        <TextButton
          onClick={() => onDismiss(false)}
          style={{ marginLeft: '-5px' }}
        >
          Dismiss
        </TextButton>
      </div>
      <div className={styles.pointer} />
    </>
  );
};

const ShareFooter = ({ video, setConfirmRedo }) => {
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showNextStepsCard, setShowNextStepsCard] = useState(true);
  const [showSpritz, setShowSpritz] = useState(false);
  const dispatch = useDispatch();

  const copyLinkInfo = getCopyLinkText(video);

  const updateVideoName = name => {
    dispatch(updateVideoNameCreator(name));
  };

  return (
    <div className="flex items:center">
      <div className="flex grow:1 pr:large items:center">
        <div className="none md-block">
          <Spacer right={1}>
            <Icon icon={<SvgEditBorder />} />
          </Spacer>
        </div>
        <TitleBar
          userCanEdit={true}
          video={video}
          onTitleUpdate={updateVideoName}
          isFirstVideoShareModal={true}
        />
      </div>
      <div className="flex items:center shrink:0">
        <Spacer right={2}>
          <TextButton
            icon={<SvgUndo />}
            onClick={() => {
              setConfirmRedo(true);
              analytics.track(FIRST_VIDEO_SHARE_MODAL_RERECORD_BUTTON_CLICKED);
            }}
          >
            Re-record
          </TextButton>
        </Spacer>

        <div className="flex items:center relative">
          {showNextStepsCard && (
            <NextStepsCard onDismiss={setShowNextStepsCard} />
          )}

          {showSpritz && (
            <>
              <SvgInline svg={SPRITZ_SVG} className={styles.leftSpritz} />
              <SvgInline svg={SPRITZ_SVG} className={styles.rightSpritz} />
            </>
          )}

          <span
            onClick={() => {
              analytics.track(FIRST_VIDEO_SHARE_MODAL_COPY_BUTTON_CLICKED);
              setShowSpritz(true);

              setTimeout(() => {
                setShowSpritz(false);
              }, 500);
            }}
          >
            <CopyLinkSection
              withTopMargin={false}
              width="100%"
              {...copyLinkInfo}
              buttonStyles={styles.copyButton}
              buttonSize="large"
            />
          </span>

          <IconButton
            className={styles.shareIcon}
            iconColor="white"
            altText="Share options"
            icon={<SvgChevronSmallDown />}
            onClick={() => {
              setShowShareOptions(!showShareOptions);
              analytics.track(
                FIRST_VIDEO_SHARE_MODAL_COPY_BUTTON_DROPDOWN_CLICKED
              );
            }}
          />

          {showShareOptions && (
            <div className="relative">
              <ShareModal
                video={video}
                shouldOpenAsDropdown={true}
                closeModal={() => setShowShareOptions(!showShareOptions)}
                isFirstVideoShareModal={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ShareFirstVideoModal = ({ video, recordingClient }) => {
  const [showConfetti, setShowConfetti] = useState(false);

  const isOpen = useSelector(selectFirstVideoShareModalIsOpen);
  const [confirmRedo, setConfirmRedo] = useState(false);

  const embedParams = ['hide_speed=true', 'hideEmbedTopBar=true'];
  const embedLink = `${LOOM_URI}${EMBED_VIDEO}/${video.id}?${embedParams.join(
    '&'
  )}`;

  const hasShowShareModalFlag = useFlagIsActivated({
    flag: EXP_FIRST_VIDEO_SHARE_MODAL,
    activationValues: ['variant'],
  });
  const dispatch = useDispatch();

  useEffect(() => {
    if (hasShowShareModalFlag) {
      dispatch(toggleFirstVideoShareModalIsOpen(true));
    }

    // We need to update the trigger status so the user only sees this modal once,
    // but we need to wait until the flag status is fulfilled or this modal
    // will be immediately removed by the wrapper component.
    if (typeof hasShowShareModalFlag !== 'undefined') {
      dispatch(completeTriggerCreator(SHOW_FIRST_VIDEO_SHARE_MODAL));
    }
  }, [hasShowShareModalFlag, dispatch]);

  const redo = () => {
    switch (recordingClient) {
      case EXTENSION:
        requestExtensionOpenIfInstalled('first-video-share-modal', success => {
          if (!success) {
            logger.error(
              'Unable to re-record with the browser extension from the first video share modal'
            );

            dispatch(
              showErrorBar(
                ERROR,
                'An error occurred attempting to start a recording'
              )
            );
          }
        });
        break;
      case DESKTOP:
        document.location.href = 'loomdesktop://';
        break;
      default:
      // This modal should only be displayed if the recording was done with either the
      // extension or desktop app
    }

    dispatch(toggleFirstVideoShareModalIsOpen(false));
  };

  setTimeout(() => {
    setShowConfetti(true);
  }, 1500);

  const confettiLeft = ReactDOM.createPortal(
    <div className={styles.bottomLeft}>
      <Confetti
        active={showConfetti}
        config={{
          angle: 55,
          spread: 55,
          startVelocity: 95,
          elementCount: 150,
          duration: 4000,
          stagger: 5,
          width: '14px',
          height: '16px',
        }}
      />
    </div>,
    document.body
  );

  const confettiRight = ReactDOM.createPortal(
    <div className={styles.bottomRight}>
      <Confetti
        active={showConfetti}
        config={{
          angle: 125,
          spread: 55,
          startVelocity: 95,
          elementCount: 150,
          duration: 4000,
          stagger: 5,
          width: '16px',
          height: '14px',
        }}
      />
    </div>,
    document.body
  );

  if (!hasShowShareModalFlag) {
    return null;
  }

  return (
    <div className="none sm-block relative">
      <Backdrop
        isOpen={isOpen}
        onClick={() => {
          dispatch(toggleFirstVideoShareModalIsOpen(false));
        }}
      >
        <div className={styles.modalWrapper}>
          <div onClick={e => e.stopPropagation()} className={styles.modal}>
            <div className={classnames(styles.playerWrapper, 'theme-dark')}>
              <Align alignment="topRight">
                <IconButton
                  icon={<SvgClose />}
                  onClick={() => {
                    dispatch(toggleFirstVideoShareModalIsOpen(false));
                  }}
                />
              </Align>
              <div className={styles.videoPlayer}>
                <div className={styles.iframeWrapper}>
                  <iframe
                    src={embedLink}
                    frameBorder="0"
                    className={styles.iframe}
                  ></iframe>
                </div>
              </div>
            </div>
            <Container backgroundColor="background" className={styles.footer}>
              {confirmRedo ? (
                <ConfirmRedoFooter
                  setConfirmRedo={setConfirmRedo}
                  redo={redo}
                />
              ) : (
                <ShareFooter video={video} setConfirmRedo={setConfirmRedo} />
              )}
            </Container>
          </div>
        </div>
      </Backdrop>
      {confettiLeft}
      {confettiRight}
    </div>
  );
};

export default ShareFirstVideoModalWrapper;
